import React from 'react';
import { siteConfig } from '@/data/site';

// ============================================
// Type Definitions
// ============================================

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  canonicalUrl?: string;
  noindex?: boolean;
  /** Custom schema to override or extend default schemas - can be a single schema or array of schemas */
  schema?: Record<string, unknown> | Record<string, unknown>[];
  /** AI-friendly metadata */
  author?: string;
  datePublished?: string;
  dateModified?: string;
  /** Page type for automatic schema generation (reserved for future use) */
  pageType?: 'home' | 'product' | 'productList' | 'about' | 'contact' | 'download' | 'application' | 'article' | 'default';
  /** Additional meta tags */
  additionalMeta?: Array<{ name?: string; property?: string; content: string }>;
  /** Language code */
  lang?: string;
  /** Breadcrumb items for this page */
  breadcrumbs?: { name: string; url?: string }[];
  /** Page-specific content for WebPage schema */
  pageContent?: {
    headline?: string;
    articleBody?: string;
    wordCount?: number;
  };
}

interface ProductInfo {
  name: string;
  model?: string;
  description: string;
  image: string;
  category?: string;
  sku?: string;
  brand?: string;
  manufacturer?: string;
  url?: string;
  offers?: {
    price?: number;
    priceCurrency?: string;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
    url?: string;
  };
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
  };
}

interface ListItem {
  name: string;
  description?: string;
  url?: string;
  image?: string;
}

interface ArticleInfo {
  headline: string;
  description: string;
  image: string;
  author?: string;
  datePublished: string;
  dateModified?: string;
  articleBody?: string;
  wordCount?: number;
  keywords?: string[];
}

interface FAQItem {
  question: string;
  answer: string;
}

// ============================================
// Main SEO Component
// ============================================

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  ogImage = '/images/og-default.jpg',
  ogType = 'website',
  canonicalUrl,
  noindex = false,
  schema,
  author = siteConfig.name,
  datePublished,
  dateModified,
  pageType = 'default',
  additionalMeta = [],
  lang = 'en',
  breadcrumbs,
  pageContent
}) => {
  const fullTitle = title ? `${title} | ${siteConfig.name}` : `${siteConfig.name} - ${siteConfig.tagline}`;
  const fullDescription = description || siteConfig.tagline;
  const fullCanonicalUrl = canonicalUrl || siteConfig.url;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteConfig.url}${ogImage}`;
  const currentDate = new Date().toISOString();

  // ============================================
  // Schema Generation
  // ============================================

  // Organization Schema - Core entity information
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteConfig.url}/#organization`,
    name: siteConfig.name,
    alternateName: 'KeyGoTechnology',
    url: siteConfig.url,
    logo: {
      '@type': 'ImageObject',
      url: `${siteConfig.url}/images/logo.png`,
      width: 512,
      height: 512
    },
    email: siteConfig.email,
    telephone: siteConfig.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteConfig.address.street,
      addressLocality: siteConfig.address.city,
      addressRegion: siteConfig.address.city,
      addressCountry: siteConfig.address.country,
      postalCode: siteConfig.address.zip
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.phone,
      contactType: 'sales',
      email: siteConfig.email,
      availableLanguage: ['English', 'Chinese']
    },
    sameAs: [
      siteConfig.social.linkedin,
      siteConfig.social.youtube,
      siteConfig.social.twitter
    ].filter(Boolean),
    description: siteConfig.tagline
  };

  // WebSite Schema - Site structure for AI understanding
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteConfig.url}/#website`,
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.tagline,
    publisher: {
      '@id': `${siteConfig.url}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteConfig.url}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    inLanguage: lang
  };

  // WebPage Schema - Current page information
  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${fullCanonicalUrl}#webpage`,
    url: fullCanonicalUrl,
    name: fullTitle,
    headline: pageContent?.headline || title || siteConfig.name,
    description: fullDescription,
    image: fullOgImage,
    author: {
      '@type': author === siteConfig.name ? 'Organization' : 'Person',
      name: author,
      ...(author === siteConfig.name && { '@id': `${siteConfig.url}/#organization` })
    },
    publisher: {
      '@id': `${siteConfig.url}/#organization`
    },
    datePublished: datePublished || currentDate,
    dateModified: dateModified || datePublished || currentDate,
    inLanguage: lang,
    isPartOf: {
      '@id': `${siteConfig.url}/#website`
    },
    ...(pageContent?.articleBody && {
      articleBody: pageContent.articleBody,
      wordCount: pageContent.wordCount
    }),
    ...(breadcrumbs && {
      breadcrumb: {
        '@id': `${fullCanonicalUrl}#breadcrumb`
      }
    })
  };

  // Use pageType to determine if we need special handling (currently reserved for future use)
  void pageType; // Marks pageType as intentionally used

  // Combine schemas based on page type
  const getCombinedSchema = (): Record<string, unknown>[] => {
    const baseSchemas: Record<string, unknown>[] = [organizationSchema, websiteSchema, webpageSchema];
    
    if (schema) {
      // If custom schema provided, merge it with base schemas
      if (Array.isArray(schema)) {
        return [...baseSchemas, ...schema];
      }
      return [...baseSchemas, schema];
    }

    // Add Breadcrumb schema if breadcrumbs provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      baseSchemas.push(generateBreadcrumbSchema(breadcrumbs) as Record<string, unknown>);
    }

    return baseSchemas;
  };

  // ============================================
  // Render
  // ============================================

  return (
    <>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* AI-Friendly Metadata */}
      <meta name="author" content={author} />
      {datePublished && (
        <meta name="datePublished" content={datePublished} />
      )}
      {dateModified && (
        <meta name="dateModified" content={dateModified} />
      )}
      <meta name="language" content={lang} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* Robots - Enhanced for AI crawlers */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="bingbot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        </>
      )}
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:locale" content={lang === 'zh' ? 'zh_CN' : 'en_US'} />
      {datePublished && (
        <meta property="article:published_time" content={datePublished} />
      )}
      {dateModified && (
        <meta property="article:modified_time" content={dateModified} />
      )}
      {author && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content={siteConfig.social.twitter ? `@${siteConfig.social.twitter.split('/').pop()}` : undefined} />
      
      {/* AI Crawler Specific */}
      <meta name="ia:markup_url" content={fullCanonicalUrl} />
      <meta name="ia:rules_url" content={`${siteConfig.url}/ai-rules.txt`} />
      
      {/* Additional Meta Tags */}
      {additionalMeta.map((meta, index) => (
        meta.property ? (
          <meta key={index} property={meta.property} content={meta.content} />
        ) : (
          <meta key={index} name={meta.name} content={meta.content} />
        )
      ))}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getCombinedSchema())}
      </script>
    </>
  );
};

// ============================================
// Helper Functions - Product Schemas
// ============================================

/**
 * Generate Product Schema for individual product pages
 */
export const generateProductSchema = (product: ProductInfo): Record<string, unknown> => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image.startsWith('http') ? product.image : `${siteConfig.url}${product.image}`,
    brand: {
      '@type': 'Brand',
      name: product.brand || siteConfig.name
    },
    manufacturer: {
      '@type': 'Organization',
      name: product.manufacturer || siteConfig.name,
      '@id': `${siteConfig.url}/#organization`
    },
    url: product.url || `${siteConfig.url}/products`,
    ...(product.model && { model: product.model }),
    ...(product.sku && { sku: product.sku }),
    ...(product.category && { category: product.category }),
    ...(product.aggregateRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.aggregateRating.ratingValue,
        reviewCount: product.aggregateRating.reviewCount
      }
    }),
    ...(product.offers && {
      offers: {
        '@type': 'Offer',
        url: product.offers.url || product.url,
        priceCurrency: product.offers.priceCurrency || 'USD',
        ...(product.offers.price && { price: product.offers.price }),
        availability: `https://schema.org/${product.offers.availability || 'InStock'}`,
        seller: {
          '@type': 'Organization',
          name: siteConfig.name,
          '@id': `${siteConfig.url}/#organization`
        }
      }
    })
  };

  return schema;
};

/**
 * Generate ItemList Schema for product list pages
 */
export const generateItemListSchema = (
  items: ListItem[],
  listName?: string
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName || 'Product List',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Thing',
        name: item.name,
        ...(item.description && { description: item.description }),
        ...(item.url && { url: item.url.startsWith('http') ? item.url : `${siteConfig.url}${item.url}` }),
        ...(item.image && { image: item.image.startsWith('http') ? item.image : `${siteConfig.url}${item.image}` })
      }
    })),
    numberOfItems: items.length
  };
};

/**
 * Generate BreadcrumbList Schema
 */
export const generateBreadcrumbSchema = (
  items: { name: string; url?: string }[]
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${siteConfig.url}/#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url && {
        item: item.url.startsWith('http') ? item.url : `${siteConfig.url}${item.url}`
      })
    }))
  };
};

// ============================================
// Helper Functions - Page Type Schemas
// ============================================

/**
 * Generate AboutPage Schema
 */
export const generateAboutPageSchema = (
  title: string,
  description: string,
  url?: string,
  mainContent?: string
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: title,
    description: description,
    url: url || `${siteConfig.url}/about`,
    mainEntity: {
      '@type': 'Organization',
      '@id': `${siteConfig.url}/#organization`
    },
    ...(mainContent && { mainContentOfPage: mainContent }),
    breadcrumb: {
      '@id': `${url || `${siteConfig.url}/about`}#breadcrumb`
    }
  };
};

/**
 * Generate ContactPage Schema
 */
export const generateContactPageSchema = (
  title: string = 'Contact Us',
  url?: string
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: title,
    url: url || `${siteConfig.url}/contact`,
    mainEntity: {
      '@type': 'Organization',
      '@id': `${siteConfig.url}/#organization`,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: siteConfig.phone,
        contactType: 'customer service',
        email: siteConfig.email,
        availableLanguage: ['English', 'Chinese'],
        areaServed: 'Worldwide'
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteConfig.address.street,
        addressLocality: siteConfig.address.city,
        addressCountry: siteConfig.address.country,
        postalCode: siteConfig.address.zip
      }
    },
    breadcrumb: {
      '@id': `${url || `${siteConfig.url}/contact`}#breadcrumb`
    }
  };
};

/**
 * Generate Article Schema for blog posts or news
 */
export const generateArticleSchema = (article: ArticleInfo): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    description: article.description,
    image: article.image.startsWith('http') ? article.image : `${siteConfig.url}${article.image}`,
    author: {
      '@type': article.author && article.author !== siteConfig.name ? 'Person' : 'Organization',
      name: article.author || siteConfig.name,
      ...(article.author === siteConfig.name && { '@id': `${siteConfig.url}/#organization` })
    },
    publisher: {
      '@id': `${siteConfig.url}/#organization`
    },
    datePublished: article.datePublished,
    ...(article.dateModified && { dateModified: article.dateModified }),
    ...(article.articleBody && {
      articleBody: article.articleBody,
      wordCount: article.wordCount || article.articleBody.split(/\s+/).length
    }),
    ...(article.keywords && { keywords: article.keywords.join(', ') }),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/articles`
    }
  };
};

/**
 * Generate FAQPage Schema
 */
export const generateFAQPageSchema = (
  faqs: FAQItem[],
  mainEntity?: { name: string; description: string }
): Record<string, unknown> => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  if (mainEntity) {
    schema.about = {
      '@type': 'Thing',
      name: mainEntity.name,
      description: mainEntity.description
    };
  }

  return schema;
};

/**
 * Generate SoftwareApplication Schema
 */
export const generateSoftwareSchema = (
  name: string,
  description: string,
  applicationCategory: string,
  operatingSystem: string,
  downloadUrl?: string,
  fileSize?: string,
  softwareVersion?: string
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    applicationCategory,
    operatingSystem,
    ...(softwareVersion && { softwareVersion }),
    ...(downloadUrl && { downloadUrl }),
    ...(fileSize && { fileSize }),
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
      '@id': `${siteConfig.url}/#organization`
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
  };
};

/**
 * Generate WebSite Schema with search action
 */
export const generateWebSiteSchema = (
  searchUrlTemplate?: string
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteConfig.url}/#website`,
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.tagline,
    publisher: {
      '@id': `${siteConfig.url}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: searchUrlTemplate || `${siteConfig.url}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
};

/**
 * Generate Organization Schema
 */
export const generateOrganizationSchema = (): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteConfig.url}/#organization`,
    name: siteConfig.name,
    alternateName: 'KeyGoTechnology',
    url: siteConfig.url,
    logo: {
      '@type': 'ImageObject',
      url: `${siteConfig.url}/images/logo.png`,
      width: 512,
      height: 512
    },
    email: siteConfig.email,
    telephone: siteConfig.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteConfig.address.street,
      addressLocality: siteConfig.address.city,
      addressCountry: siteConfig.address.country,
      postalCode: siteConfig.address.zip
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.phone,
      contactType: 'sales',
      email: siteConfig.email,
      availableLanguage: ['English', 'Chinese']
    },
    sameAs: [
      siteConfig.social.linkedin,
      siteConfig.social.youtube,
      siteConfig.social.twitter
    ].filter(Boolean),
    description: siteConfig.tagline
  };
};

/**
 * Generate Service Schema for application areas
 */
export const generateServiceSchema = (
  name: string,
  description: string,
  provider?: string,
  areaServed?: string,
  serviceType?: string
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: provider || siteConfig.name,
      '@id': `${siteConfig.url}/#organization`
    },
    ...(areaServed && { areaServed }),
    ...(serviceType && { serviceType })
  };
};

/**
 * Generate ImageObject Schema for images
 */
export const generateImageObjectSchema = (
  url: string,
  caption?: string,
  width?: number,
  height?: number,
  author?: string
): Record<string, unknown> => {
  const fullUrl = url.startsWith('http') ? url : `${siteConfig.url}${url}`;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: fullUrl,
    url: fullUrl,
    ...(caption && { caption, description: caption }),
    ...(width && { width }),
    ...(height && { height }),
    ...(author && {
      author: {
        '@type': author === siteConfig.name ? 'Organization' : 'Person',
        name: author
      }
    }),
    license: `${siteConfig.url}/terms`,
    acquireLicensePage: `${siteConfig.url}/contact`
  };
};

/**
 * Combine multiple schemas for a page
 */
export const combineSchemas = (
  ...schemas: Record<string, unknown>[]
): Record<string, unknown>[] => {
  return schemas;
};

/**
 * Generate complete page schema with Organization, WebSite, WebPage and optional extras
 */
export const generateCompletePageSchema = (
  pageUrl: string,
  pageTitle: string,
  pageDescription: string,
  options?: {
    author?: string;
    datePublished?: string;
    dateModified?: string;
    breadcrumbs?: { name: string; url?: string }[];
    extraSchemas?: Record<string, unknown>[];
  }
): Record<string, unknown>[] => {
  const fullUrl = pageUrl.startsWith('http') ? pageUrl : `${siteConfig.url}${pageUrl}`;
  const currentDate = new Date().toISOString();
  
  const schemas: Record<string, unknown>[] = [
    generateOrganizationSchema(),
    generateWebSiteSchema(),
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${fullUrl}#webpage`,
      url: fullUrl,
      name: pageTitle,
      description: pageDescription,
      author: {
        '@type': options?.author && options.author !== siteConfig.name ? 'Person' : 'Organization',
        name: options?.author || siteConfig.name
      },
      datePublished: options?.datePublished || currentDate,
      dateModified: options?.dateModified || options?.datePublished || currentDate
    }
  ];

  if (options?.breadcrumbs) {
    schemas.push(generateBreadcrumbSchema(options.breadcrumbs));
  }

  if (options?.extraSchemas) {
    schemas.push(...options.extraSchemas);
  }

  return schemas;
};

export default SEO;
